import { Box, InputAdornment, TextField } from "@mui/material";
import { ReactNode, useCallback, useEffect, useState } from "react";

const DEFAULT_UPPER = 0.01;
const DEFAULT_LOWER = 0.1;

/** Parse "+.010/-.100" → { upper: 0.01, lower: 0.1 } */
function parseTolerance(val: string | null): {
	upper: number | null;
	lower: number | null;
} {
	if (!val || !val.trim()) return { upper: null, lower: null };

	const m = val.match(
		/^\+?\s*([0-9]*\.?[0-9]+)\s*\/\s*-\s*([0-9]*\.?[0-9]+)\s*$/,
	);
	if (m) return { upper: parseFloat(m[1]), lower: parseFloat(m[2]) };

	return { upper: null, lower: null };
}

/** Compose { upper: 0.01, lower: 0.1 } → "+0.010/-0.100" */
function composeTolerance(
	upper: number | null,
	lower: number | null,
): string | null {
	if (upper == null && lower == null) return null;
	const u = upper != null ? `+${upper.toFixed(3)}` : "+0.000";
	const l = lower != null ? `-${lower.toFixed(3)}` : "-0.000";
	return `${u}/${l}`;
}

type Props = {
	value: string | null;
	onChange: (val: string | null) => void;
	disabled?: boolean;
	maxLimit?: number;
	/**
	 * When true, render empty inputs while value is null/unparsed instead of
	 * pre-filling DEFAULT_UPPER / DEFAULT_LOWER. Opt-in to avoid changing
	 * existing call sites that rely on the default-filled UX.
	 */
	emptyOnNull?: boolean;
	/**
	 * Target field rendered between the limits, switching the order to
	 * Lower (min) | middle | Upper (max) so the row reads like the spec.
	 */
	middle?: ReactNode;
};

export const TolerancePicker = ({
	value,
	onChange,
	disabled = false,
	maxLimit,
	emptyOnNull = false,
	middle,
}: Props) => {
	const fallbackUpper = emptyOnNull ? "" : String(DEFAULT_UPPER);
	const fallbackLower = emptyOnNull ? "" : String(DEFAULT_LOWER);

	const [upper, setUpper] = useState<string>(() => {
		const p = parseTolerance(value);
		return p.upper != null ? String(p.upper) : fallbackUpper;
	});
	const [lower, setLower] = useState<string>(() => {
		const p = parseTolerance(value);
		return p.lower != null ? String(p.lower) : fallbackLower;
	});

	// Sync when external value changes (form reset, product switch)
	useEffect(() => {
		const p = parseTolerance(value);
		setUpper(p.upper != null ? String(p.upper) : fallbackUpper);
		setLower(p.lower != null ? String(p.lower) : fallbackLower);
	}, [value, fallbackUpper, fallbackLower]);

	const handleChange = useCallback(
		(side: "upper" | "lower", raw: string) => {
			const num = raw === "" ? null : parseFloat(raw);
			if (side === "upper") {
				setUpper(raw);
				const l = lower === "" ? null : parseFloat(lower);
				onChange(composeTolerance(num, l));
			} else {
				setLower(raw);
				const u = upper === "" ? null : parseFloat(upper);
				onChange(composeTolerance(u, num));
			}
		},
		[upper, lower, onChange],
	);

	const handleBlur = useCallback(
		(side: "upper" | "lower") => {
			const raw = side === "upper" ? upper : lower;
			if (raw === "") return;
			const n = parseFloat(raw);
			if (!Number.isFinite(n)) return;
			const rounded = Math.round(n * 1000) / 1000;
			const s = String(rounded);
			if (s === raw) return;
			if (side === "upper") {
				setUpper(s);
				const l = lower === "" ? null : parseFloat(lower);
				onChange(composeTolerance(rounded, l));
			} else {
				setLower(s);
				const u = upper === "" ? null : parseFloat(upper);
				onChange(composeTolerance(u, rounded));
			}
		},
		[upper, lower, onChange],
	);

	const upperField = (
		<TextField
			label="Upper Limit"
			type="number"
			value={upper}
			onChange={(e) => handleChange("upper", e.target.value)}
			onBlur={() => handleBlur("upper")}
			size="small"
			disabled={disabled}
			fullWidth
			inputProps={{ step: 0.001, min: 0, max: maxLimit }}
			InputProps={{
				startAdornment: <InputAdornment position="start">+</InputAdornment>,
			}}
		/>
	);
	const lowerField = (
		<TextField
			label="Lower Limit"
			type="number"
			value={lower}
			onChange={(e) => handleChange("lower", e.target.value)}
			onBlur={() => handleBlur("lower")}
			size="small"
			disabled={disabled}
			fullWidth
			inputProps={{ step: 0.001, min: 0, max: maxLimit }}
			InputProps={{
				startAdornment: (
					<InputAdornment position="start">&ndash;</InputAdornment>
				),
			}}
		/>
	);

	if (middle) {
		return (
			<Box sx={{ display: "flex", gap: 1 }}>
				{lowerField}
				<Box sx={{ flex: "0 0 40%" }}>{middle}</Box>
				{upperField}
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", gap: 1 }}>
			{upperField}
			{lowerField}
		</Box>
	);
};
